import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  CrawlService,
  CrawlStatus,
  StartCrawlOptions,
  FailedPage,
  CrawlEstimate,
  DomainProcessingStatus,
  SupportingPagesBuildStatus,
  SitemapTreeNode
} from '../../services/crawl';
import { NotificationService } from '../../services/notification.service';
import { CrawlRunHistoryComponent } from '../crawl-run-history/crawl-run-history';
import { CrawlScheduleComponent } from '../crawl-schedule/crawl-schedule';
import { Subscription, interval, firstValueFrom } from 'rxjs';

interface DomainCrawlStatus {
  domain: string;
  status: CrawlStatus | null;
  loading: boolean;
  error: string | null;
}

@Component({
  selector: 'app-crawl-management',
  standalone: true,
  imports: [CommonModule, FormsModule, CrawlRunHistoryComponent, CrawlScheduleComponent],
  templateUrl: './crawl-management.html',
  styleUrl: './crawl-management.css'
})
export class CrawlManagementComponent implements OnInit, OnDestroy {
  private readonly defaultStartCrawlOptions: StartCrawlOptions = {
    limit: 750,
    priority: 'high',
    frequency: 'manual',
    enableBrowserRendering: false
  };

  domains: string[] = [];
  domainStatuses: Map<string, DomainCrawlStatus> = new Map();
  loadingDomains = false;
  error: string | null = null;

  newDomainInput = '';
  newDomainEstimate: CrawlEstimate | null = null;
  newDomainEstimateLoading = false;
  newDomainEstimateError: string | null = null;
  newDomainStartLoading = false;
  newDomainStartError: string | null = null;
  minLastmodFrom: string = '';
  perSitemapLimits: Record<string, number> = {};
  perSitemapLimitMode: 'recent' | 'oldest' | 'balanced' = 'recent';
  selectedSitemaps: Set<string> = new Set();
  availableLeafSitemaps: Array<{ sitemapUrl: string; urlCount: number }> = [];
  
  failedPages: Map<string, FailedPage[]> = new Map();
  failedPagesLoading: Map<string, boolean> = new Map();
  noindexPagesExpanded: Map<string, boolean> = new Map();
  failedPagesExpanded: Map<string, boolean> = new Map();
  deletingDomains: Map<string, boolean> = new Map();
  processingStatuses: Map<string, DomainProcessingStatus> = new Map();
  trafficRefreshing: Map<string, boolean> = new Map();
  keywordsRefreshing: Map<string, boolean> = new Map();
  internalLinksRefreshing: Map<string, boolean> = new Map();
  supportingPagesByTopicRefreshing: Map<string, boolean> = new Map();
  supportingPagesOpportunitiesRefreshing: Map<string, boolean> = new Map();

  private readonly API_URL = environment.apiUrl + '/v1';
  private enrichmentSubs: Subscription = new Subscription();

  private pollingSubscriptions: Map<string, Subscription> = new Map();
  private enrichmentPollingSubscriptions: Map<string, Subscription> = new Map();
  private readonly POLL_INTERVAL = 10000; // 10 seconds
  private readonly ENRICHMENT_POLL_INTERVAL = 3000;
  /** Avoid thundering herd when many managed domains load status at once. */
  private readonly CRAWL_STATUS_BATCH_SIZE = 6;

  constructor(
    private crawlService: CrawlService,
    private http: HttpClient,
    private notifications: NotificationService
  ) {}

  private normalizeDomain(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  ngOnInit() {
    this.loadDomains();
  }

  ngOnDestroy() {
    this.pollingSubscriptions.forEach(sub => sub.unsubscribe());
    this.pollingSubscriptions.clear();
    this.enrichmentPollingSubscriptions.forEach(sub => sub.unsubscribe());
    this.enrichmentPollingSubscriptions.clear();
    this.enrichmentSubs.unsubscribe();
  }

  async loadDomains() {
    this.loadingDomains = true;
    this.error = null;

    try {
      const response = await firstValueFrom(this.crawlService.getCrawlManagedDomains());
      if (response?.success && response.data) {
        this.domains = response.data.sort();
        
        // Initialize status for each domain
        this.domains.forEach(domain => {
          if (!this.domainStatuses.has(domain)) {
            this.domainStatuses.set(domain, {
              domain,
              status: null,
              loading: false,
              error: null
            });
          }
        });

        // Load status in batches so Setup/other routes stay responsive
        await this.loadAllCrawlStatuses(this.domains);
      }
    } catch (error: any) {
      console.error('Failed to load domains:', error);
      this.error = error.message || 'Failed to load domains';
    } finally {
      this.loadingDomains = false;
    }
  }

  private async loadAllCrawlStatuses(domains: string[]): Promise<void> {
    const batchSize = this.CRAWL_STATUS_BATCH_SIZE;
    for (let i = 0; i < domains.length; i += batchSize) {
      const batch = domains.slice(i, i + batchSize);
      await Promise.all(batch.map((domain) => this.loadCrawlStatus(domain)));
    }
  }

  async loadCrawlStatus(domain: string) {
    const domainStatus = this.domainStatuses.get(domain);
    if (!domainStatus) return;

    domainStatus.loading = true;
    domainStatus.error = null;

    try {
      const [status, processing] = await Promise.allSettled([
        firstValueFrom(this.crawlService.getCrawlStatus(domain)),
        firstValueFrom(this.crawlService.getProcessingStatus(domain))
      ]);

      if (status.status === 'fulfilled' && status.value) {
        domainStatus.status = status.value;
        if (!this.isCrawlComplete(status.value)) {
          this.startPolling(domain);
        } else {
          this.stopPolling(domain);
        }
      }

      if (processing.status === 'fulfilled' && processing.value) {
        this.processingStatuses.set(
          domain,
          this.mergePendingSupportingPagesBuildStatus(domain, processing.value)
        );
        this.clearSupportingPagesBuildRefreshingIfIdle(domain);
        if (this.isEnrichmentBuildRunning(this.processingStatuses.get(domain)!)) {
          this.startEnrichmentPolling(domain);
        } else {
          this.stopEnrichmentPolling(domain);
        }
      } else if (processing.status === 'rejected') {
        const reason = (processing.reason as any)?.error?.message
          || (processing.reason as any)?.message
          || 'Could not load processing status.';
        console.error(`Processing status failed for ${domain}:`, processing.reason);
        if (!domainStatus.error) {
          domainStatus.error = reason;
        }
      }
    } catch (error: any) {
      console.error(`Failed to load crawl status for ${domain}:`, error);
      domainStatus.error = error.message || 'Failed to load crawl status';
    } finally {
      domainStatus.loading = false;
    }
  }

  async startCrawl(domain: string) {
    const domainStatus = this.domainStatuses.get(domain);
    if (!domainStatus) return;

    domainStatus.loading = true;
    domainStatus.error = null;

    try {
      const response = await firstValueFrom(
        this.crawlService.startCrawl(domain, this.defaultStartCrawlOptions)
      );

      if (response?.success) {
        // Reload status after starting crawl
        await this.loadCrawlStatus(domain);
      } else {
        domainStatus.error = response?.message || 'Failed to start crawl';
      }
    } catch (error: any) {
      console.error(`Failed to start crawl for ${domain}:`, error);
      domainStatus.error = error.error?.message || error.message || 'Failed to start crawl';
    } finally {
      domainStatus.loading = false;
    }
  }

  async estimateNewDomain(preserveSelection: boolean = false): Promise<void> {
    const normalized = this.normalizeDomain(this.newDomainInput);
    if (!normalized) {
      this.newDomainEstimateError = 'Please enter a valid domain.';
      this.newDomainEstimate = null;
      return;
    }

    this.newDomainEstimateLoading = true;
    this.newDomainEstimateError = null;

    try {
      const response = await firstValueFrom(
        this.crawlService.estimateCrawl(normalized, {
          limit: this.defaultStartCrawlOptions.limit,
          selectedSitemaps: preserveSelection ? Array.from(this.selectedSitemaps) : [],
          minLastmodFrom: this.minLastmodFrom || null,
          perSitemapLimits: this.perSitemapLimits,
          perSitemapLimitMode: this.perSitemapLimitMode
        })
      );
      if (response?.success && response.data) {
        const previousSelection = new Set(this.selectedSitemaps);
        if (!preserveSelection) {
          this.perSitemapLimits = {};
        }
        this.newDomainEstimate = response.data;
        this.availableLeafSitemaps = this.collectLeafSitemaps(response.data.sitemapTree || []);
        const validLeafSet = new Set(this.availableLeafSitemaps.map((entry) => entry.sitemapUrl));
        this.perSitemapLimits = Object.fromEntries(
          Object.entries(this.perSitemapLimits).filter(([sitemapUrl, value]) => validLeafSet.has(sitemapUrl) && value > 0)
        );
        if (preserveSelection) {
          this.selectedSitemaps = new Set(
            this.availableLeafSitemaps
              .map((entry) => entry.sitemapUrl)
              .filter((url) => previousSelection.has(url))
          );
          if (this.selectedSitemaps.size === 0 && this.availableLeafSitemaps.length > 0) {
            this.selectedSitemaps.add(this.availableLeafSitemaps[0].sitemapUrl);
          }
        } else {
          this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((entry) => entry.sitemapUrl));
        }
      } else {
        this.newDomainEstimate = null;
        this.newDomainEstimateError = 'Could not read sitemap for that domain.';
      }
    } catch (err: any) {
      this.newDomainEstimate = null;
      this.newDomainEstimateError =
        err?.error?.message || err?.message || 'Could not read sitemap for that domain.';
    } finally {
      this.newDomainEstimateLoading = false;
    }
  }

  async startCrawlForNewDomain(): Promise<void> {
    const normalized = this.normalizeDomain(this.newDomainInput);
    if (!normalized) {
      this.newDomainStartError = 'Please enter a valid domain.';
      return;
    }

    this.newDomainStartLoading = true;
    this.newDomainStartError = null;

    try {
      const response = await firstValueFrom(
        this.crawlService.startCrawl(normalized, {
          ...this.defaultStartCrawlOptions,
          selectedSitemaps: Array.from(this.selectedSitemaps),
          minLastmodFrom: this.minLastmodFrom || null,
          perSitemapLimits: this.perSitemapLimits,
          perSitemapLimitMode: this.perSitemapLimitMode
        })
      );

      if (response?.success) {
        await this.loadDomains();
      } else {
        this.newDomainStartError = response?.message || 'Failed to start crawl.';
      }
    } catch (err: any) {
      this.newDomainStartError =
        err?.error?.message || err?.message || 'Failed to start crawl.';
    } finally {
      this.newDomainStartLoading = false;
    }
  }

  private collectLeafSitemaps(nodes: SitemapTreeNode[]): Array<{ sitemapUrl: string; urlCount: number }> {
    const leaves: Array<{ sitemapUrl: string; urlCount: number }> = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') {
        leaves.push({ sitemapUrl: node.sitemapUrl, urlCount: node.urlCount || 0 });
      }
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getSelectedDiscoveredUrlCount(): number {
    return this.newDomainEstimate?.selectedPagesInSitemap ?? 0;
  }

  getSelectedIncludedAtCap(): number {
    if (!this.newDomainEstimate) return 0;
    return this.newDomainEstimate.selectedFilteredPages ?? 0;
  }

  getSelectedEstimatedCost(): number {
    if (!this.newDomainEstimate) return 0;
    return this.newDomainEstimate.selectedEstimatedTotalCost
      ?? (this.getSelectedIncludedAtCap() * this.newDomainEstimate.estimatedCostPerPage);
  }

  hasSitemapSelection(): boolean {
    return this.selectedSitemaps.size > 0;
  }

  toggleSitemapSelection(sitemapUrl: string, checked: boolean): void {
    if (checked) {
      this.selectedSitemaps.add(sitemapUrl);
      this.refreshEstimateForFilters();
      return;
    }
    this.selectedSitemaps.delete(sitemapUrl);
    this.refreshEstimateForFilters();
  }

  setAllSitemapsSelected(checked: boolean): void {
    if (checked) {
      this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((entry) => entry.sitemapUrl));
      this.refreshEstimateForFilters();
      return;
    }
    this.selectedSitemaps.clear();
    this.refreshEstimateForFilters();
  }

  areAllSitemapsSelected(): boolean {
    return this.availableLeafSitemaps.length > 0 && this.selectedSitemaps.size === this.availableLeafSitemaps.length;
  }

  getLeafNodes(nodes: SitemapTreeNode[] | undefined): SitemapTreeNode[] {
    if (!nodes || nodes.length === 0) return [];
    const leaves: SitemapTreeNode[] = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') leaves.push(node);
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getLeafDisplayUrlCount(node: SitemapTreeNode): number {
    const base = this.minLastmodFrom ? (node.filteredUrlCount ?? 0) : (node.urlCount || 0);
    const cap = this.getPerSitemapLimit(node.sitemapUrl);
    return cap ? Math.min(base, cap) : base;
  }

  getPerSitemapLimit(sitemapUrl: string): number | null {
    const value = this.perSitemapLimits[sitemapUrl];
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  onPerSitemapLimitChange(sitemapUrl: string, value: string | number | null): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.perSitemapLimits[sitemapUrl] = Math.floor(parsed);
    } else {
      delete this.perSitemapLimits[sitemapUrl];
    }
    this.refreshEstimateForFilters();
  }

  onMinLastmodFromChange(value: string): void {
    this.minLastmodFrom = value;
    this.refreshEstimateForFilters();
  }

  onPerSitemapLimitModeChange(value: 'recent' | 'oldest' | 'balanced'): void {
    this.perSitemapLimitMode = value;
    this.refreshEstimateForFilters();
  }

  private refreshEstimateForFilters(): void {
    if (!this.newDomainEstimate || this.newDomainEstimateLoading) return;
    this.estimateNewDomain(true);
  }

  startPolling(domain: string) {
    // Stop existing polling if any
    this.stopPolling(domain);

    // Start new polling
    const subscription = interval(this.POLL_INTERVAL).subscribe(() => {
      this.loadCrawlStatus(domain);
    });

    this.pollingSubscriptions.set(domain, subscription);
  }

  stopPolling(domain: string) {
    const subscription = this.pollingSubscriptions.get(domain);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(domain);
    }
  }

  private isEnrichmentBuildRunning(ps: DomainProcessingStatus): boolean {
    return ps.supportingPagesByTopic?.status === 'partial'
      || ps.supportingPagesOpportunities?.status === 'partial';
  }

  private isSupportingPagesBuildRefreshing(domain: string): boolean {
    return (
      this.supportingPagesByTopicRefreshing.get(domain) === true
      || this.supportingPagesOpportunitiesRefreshing.get(domain) === true
    );
  }

  private optimisticSupportingPagesBuildRow(
    existing: SupportingPagesBuildStatus | undefined
  ): SupportingPagesBuildStatus {
    return {
      status: 'partial',
      snapshotCount: existing?.snapshotCount ?? 0,
      totalUrls: existing?.totalUrls ?? 0,
      processedUrls: existing?.processedUrls ?? 0,
      progressPercent: existing?.progressPercent ?? 0,
      estimatedRemainingMs: existing?.estimatedRemainingMs ?? null,
      estimatedRemainingLabel: existing?.estimatedRemainingLabel ?? null,
      lastGeneratedAt: existing?.lastGeneratedAt ?? null,
      startedAt: existing?.startedAt ?? new Date().toISOString(),
      blockedReason: null,
      prerequisitesReady: true
    };
  }

  private markSupportingPagesBuildQueued(domain: string, kind: 'byTopic' | 'opportunities'): void {
    const ps = this.processingStatuses.get(domain);
    if (!ps) return;
    if (kind === 'byTopic') {
      this.processingStatuses.set(domain, {
        ...ps,
        supportingPagesByTopic: this.optimisticSupportingPagesBuildRow(ps.supportingPagesByTopic)
      });
    } else {
      this.processingStatuses.set(domain, {
        ...ps,
        supportingPagesOpportunities: this.optimisticSupportingPagesBuildRow(ps.supportingPagesOpportunities)
      });
    }
    this.startEnrichmentPolling(domain);
  }

  private mergePendingSupportingPagesBuildStatus(
    domain: string,
    ps: DomainProcessingStatus
  ): DomainProcessingStatus {
    let next = ps;
    if (
      this.supportingPagesByTopicRefreshing.get(domain)
      && ps.supportingPagesByTopic?.status === 'not_started'
    ) {
      next = {
        ...next,
        supportingPagesByTopic: this.optimisticSupportingPagesBuildRow(ps.supportingPagesByTopic)
      };
    }
    if (
      this.supportingPagesOpportunitiesRefreshing.get(domain)
      && ps.supportingPagesOpportunities?.status === 'not_started'
    ) {
      next = {
        ...next,
        supportingPagesOpportunities: this.optimisticSupportingPagesBuildRow(ps.supportingPagesOpportunities)
      };
    }
    return next;
  }

  private clearSupportingPagesBuildRefreshingIfIdle(domain: string): void {
    const ps = this.processingStatuses.get(domain);
    if (!ps) {
      this.supportingPagesByTopicRefreshing.set(domain, false);
      this.supportingPagesOpportunitiesRefreshing.set(domain, false);
      this.stopEnrichmentPolling(domain);
      return;
    }
    if (this.supportingPagesByTopicRefreshing.get(domain)) {
      const row = ps.supportingPagesByTopic;
      if (row?.status === 'complete' || row?.status === 'blocked' || row?.status === 'not_started') {
        this.supportingPagesByTopicRefreshing.set(domain, false);
      }
    }
    if (this.supportingPagesOpportunitiesRefreshing.get(domain)) {
      const row = ps.supportingPagesOpportunities;
      if (row?.status === 'complete' || row?.status === 'blocked' || row?.status === 'not_started') {
        this.supportingPagesOpportunitiesRefreshing.set(domain, false);
      }
    }
    if (!this.isEnrichmentBuildRunning(ps) && !this.isSupportingPagesBuildRefreshing(domain)) {
      this.stopEnrichmentPolling(domain);
    }
  }

  startEnrichmentPolling(domain: string) {
    if (this.enrichmentPollingSubscriptions.has(domain)) return;
    const subscription = interval(this.ENRICHMENT_POLL_INTERVAL).subscribe(() => {
      void this.loadCrawlStatus(domain);
    });
    this.enrichmentPollingSubscriptions.set(domain, subscription);
  }

  stopEnrichmentPolling(domain: string) {
    const subscription = this.enrichmentPollingSubscriptions.get(domain);
    if (subscription) {
      subscription.unsubscribe();
      this.enrichmentPollingSubscriptions.delete(domain);
    }
  }

  supportingPagesByTopicPrerequisiteHint(domain: string): string | null {
    const ps = this.processingStatuses.get(domain);
    if (!ps) return null;
    const row = ps.supportingPagesByTopic;
    if (!row) return null;
    if (row.prerequisitesReady) return null;
    return row.blockedReason || 'Run Topics aggregate and mark competitors in Settings.';
  }

  canBuildSupportingPagesByTopic(domain: string): boolean {
    const ps = this.processingStatuses.get(domain);
    return Boolean(ps?.supportingPagesByTopic?.prerequisitesReady);
  }

  supportingPagesOpportunitiesPrerequisiteHint(domain: string): string | null {
    const ps = this.processingStatuses.get(domain);
    if (!ps) return null;
    const row = ps.supportingPagesOpportunities;
    if (!row) return null;
    if (row.prerequisitesReady) return null;
    return row.blockedReason || 'Build Supporting pages by topic first; content-gap sidecar must be running.';
  }

  canBuildSupportingPagesOpportunities(domain: string): boolean {
    const ps = this.processingStatuses.get(domain);
    return Boolean(ps?.supportingPagesOpportunities?.prerequisitesReady);
  }

  buildSupportingPagesByTopic(domain: string): void {
    if (this.supportingPagesByTopicRefreshing.get(domain)) return;
    if (!this.canBuildSupportingPagesByTopic(domain)) {
      this.notifications.error(this.supportingPagesByTopicPrerequisiteHint(domain) || 'Prerequisites not met.');
      return;
    }
    this.supportingPagesByTopicRefreshing.set(domain, true);
    this.markSupportingPagesBuildQueued(domain, 'byTopic');
    this.enrichmentSubs.add(
      this.http.post<{ success: boolean; message?: string }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/supporting-pages-by-topic`,
        {}
      ).subscribe({
        next: (res) => {
          this.notifications.success(res?.message || 'Supporting pages by topic build queued.');
          void this.loadCrawlStatus(domain);
        },
        error: (err) => {
          this.supportingPagesByTopicRefreshing.set(domain, false);
          this.stopEnrichmentPolling(domain);
          this.notifications.error(err?.error?.message || err?.message || 'Failed to build supporting pages by topic.');
          void this.loadCrawlStatus(domain);
        }
      })
    );
  }

  buildSupportingPagesOpportunities(domain: string): void {
    if (this.supportingPagesOpportunitiesRefreshing.get(domain)) return;
    if (!this.canBuildSupportingPagesOpportunities(domain)) {
      this.notifications.error(this.supportingPagesOpportunitiesPrerequisiteHint(domain) || 'Prerequisites not met.');
      return;
    }
    this.supportingPagesOpportunitiesRefreshing.set(domain, true);
    this.markSupportingPagesBuildQueued(domain, 'opportunities');
    this.enrichmentSubs.add(
      this.http.post<{ success: boolean; message?: string }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/supporting-pages-opportunities`,
        {}
      ).subscribe({
        next: (res) => {
          this.notifications.success(res?.message || 'Supporting pages opportunities build queued.');
          void this.loadCrawlStatus(domain);
        },
        error: (err) => {
          this.supportingPagesOpportunitiesRefreshing.set(domain, false);
          this.stopEnrichmentPolling(domain);
          this.notifications.error(err?.error?.message || err?.message || 'Failed to build supporting pages opportunities.');
          void this.loadCrawlStatus(domain);
        }
      })
    );
  }

  isSupportingPagesByTopicRunning(domain: string, row?: SupportingPagesBuildStatus): boolean {
    return (
      this.supportingPagesByTopicRefreshing.get(domain) === true
      || this.isSupportingPagesBuildRunning(row)
    );
  }

  isSupportingPagesOpportunitiesRunning(domain: string, row?: SupportingPagesBuildStatus): boolean {
    return (
      this.supportingPagesOpportunitiesRefreshing.get(domain) === true
      || this.isSupportingPagesBuildRunning(row)
    );
  }

  supportingPagesBuildProgressLabel(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities']
  ): string {
    if (!row) return '0 snapshots';
    if (row.status === 'partial') {
      if (!row.totalUrls) return 'Starting…';
      const eta = row.estimatedRemainingLabel ? ` · ${row.estimatedRemainingLabel}` : '';
      return `${row.processedUrls} / ${row.totalUrls} URLs${eta}`;
    }
    return `${row.snapshotCount} snapshots`;
  }

  supportingPagesBuildProgressLabelRunning(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities'] | undefined,
    running: boolean
  ): string {
    if (running && row?.status !== 'partial') {
      return this.supportingPagesBuildProgressLabel(this.optimisticSupportingPagesBuildRow(row));
    }
    return this.supportingPagesBuildProgressLabel(row);
  }

  supportingPagesBuildProgressPercentRunning(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities'] | undefined,
    running: boolean
  ): number {
    if (running && row?.status !== 'partial') return 0;
    return this.supportingPagesBuildProgressPercent(row);
  }

  supportingPagesBuildProgressPercent(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities'] | undefined
  ): number {
    if (!row) return 0;
    if (row.status === 'partial') return row.progressPercent ?? 0;
    return row.status === 'complete' ? 100 : 0;
  }

  isSupportingPagesBuildRunning(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities']
  ): boolean {
    return row?.status === 'partial';
  }

  supportingPagesBuildBadge(
    row: DomainProcessingStatus['supportingPagesByTopic'] | DomainProcessingStatus['supportingPagesOpportunities'],
    running: boolean
  ): string {
    if (running && row?.status !== 'partial') return 'partial';
    return row?.status || 'not_started';
  }

  isCrawlComplete(status: CrawlStatus | null): boolean {
    if (!status) return false;
    if (status.phase === 'complete' || status.isComplete) return true;
    return false;
  }

  getProgressPercentage(status: CrawlStatus | null): number {
    if (!status || status.totalUrls === 0) return 0;
    return status.progress ?? Math.round(((status.pagesCrawled + status.pagesFailed) / status.totalUrls) * 100);
  }

  formatProgress(status: CrawlStatus | null): string {
    if (!status) return 'N/A';
    if (status.phase === 'analyzing') {
      const analyzed = status.pagesAnalyzed ?? 0;
      return `${status.pagesCrawled} crawled — analyzing ${analyzed} / ${status.totalUrls}`;
    }
    if (status.phase === 'complete') {
      const analyzed = status.pagesAnalyzed ?? status.pagesCrawled;
      return `${analyzed} / ${status.totalUrls} pages`;
    }
    const completed = status.pagesCrawled + status.pagesFailed;
    return `${completed} / ${status.totalUrls}`;
  }

  /* Returns the full class list, not a modifier, because the template binds
     it with [class] -- which REPLACES the class attribute rather than adding
     to it, so `badge` has to come back from here or it never lands. */
  getStatusClass(status: CrawlStatus | null): string {
    if (!status) return 'badge badge--danger';
    if (this.isCrawlComplete(status)) return 'badge badge--success';
    if (status.phase === 'crawling' || status.phase === 'analyzing') return 'badge badge--warning';
    if (status.pagesCrawled > 0 || status.pagesInQueue > 0) return 'badge badge--warning';
    /* Pending is the neutral tone and "unknown" above is danger, on purpose:
       they are the two states this screen is most likely to be misread on,
       and the tone is the only thing telling them apart at a glance. */
    return 'badge';
  }

  /* The enrichment rows' status string is data, not a class name -- it comes
     straight off DomainProcessingStatus. This is the one place it becomes a
     tone, so a new status shows up as a neutral badge rather than unstyled. */
  enrichmentBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'complete': return 'badge badge--success';
      case 'partial': return 'badge badge--warning';
      case 'blocked': return 'badge badge--danger';
      default: return 'badge';
    }
  }

  getStatusText(status: CrawlStatus | null): string {
    if (!status) return 'Unknown';
    if (this.isCrawlComplete(status)) return 'Complete';
    if (status.phase === 'crawling') return 'Crawling';
    if (status.phase === 'analyzing') return 'Analyzing';
    if (status.pagesCrawled > 0 || status.pagesInQueue > 0) return 'In Progress';
    return 'Not Started';
  }

  async toggleNoindexPages(domain: string) {
    await this.toggleIssuePages(domain, 'noindex');
  }

  async toggleFailedPages(domain: string) {
    await this.toggleIssuePages(domain, 'failure');
  }

  private async toggleIssuePages(domain: string, category: 'noindex' | 'failure') {
    const expandedMap = category === 'noindex' ? this.noindexPagesExpanded : this.failedPagesExpanded;
    const isExpanded = expandedMap.get(domain) || false;
    if (isExpanded) {
      expandedMap.set(domain, false);
      return;
    }

    if (!this.failedPages.has(domain)) {
      this.failedPagesLoading.set(domain, true);
      try {
        const pages = await firstValueFrom(this.crawlService.getFailedPages(domain));
        this.failedPages.set(domain, pages);
      } catch (error: any) {
        console.error(`Failed to load issue pages for ${domain}:`, error);
        this.failedPages.set(domain, []);
      } finally {
        this.failedPagesLoading.set(domain, false);
      }
    }

    expandedMap.set(domain, true);
  }

  getIssuePages(domain: string, category: 'noindex' | 'failure'): FailedPage[] {
    return (this.failedPages.get(domain) || []).filter((page) => page.category === category);
  }

  hasExpandedIssuePanel(domain: string): boolean {
    return Boolean(this.noindexPagesExpanded.get(domain) || this.failedPagesExpanded.get(domain));
  }

  refreshTraffic(domain: string): void {
    if (this.trafficRefreshing.get(domain)) return;
    this.trafficRefreshing.set(domain, true);

    this.enrichmentSubs.add(
      this.http.post<{ success: boolean; message?: string; data?: any }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/traffic-enrichment`,
        { force: true }
      ).subscribe({
        next: (res) => {
          this.trafficRefreshing.set(domain, false);
          this.notifications.success(res?.message || 'Estimated traffic refresh complete.');
          this.loadCrawlStatus(domain);
        },
        error: (err) => {
          this.trafficRefreshing.set(domain, false);
          this.notifications.error(err?.error?.message || err?.message || 'Failed to refresh estimated traffic.');
        }
      })
    );
  }

  hasProcessingStatus(domain: string): boolean {
    return this.processingStatuses.has(domain);
  }

  internalLinksPrerequisiteHint(domain: string): string | null {
    const ps = this.processingStatuses.get(domain);
    if (!ps) return null;
    const ils = ps.internalLinkSuggestions;
    if (!ils) return null;
    if (ils.prerequisitesReady) return null;
    return ils.blockedReason || 'Run Links classify and Topics aggregate after crawl.';
  }

  processingStatusLoadHint(domain: string): string | null {
    if (this.hasProcessingStatus(domain)) return null;
    return 'Enrichment rows did not load — click Refresh on this card (or restart the backend after deploying).';
  }

  canRefreshInternalLinks(domain: string): boolean {
    const ps = this.processingStatuses.get(domain);
    return Boolean(ps?.internalLinkSuggestions?.prerequisitesReady);
  }

  refreshInternalLinks(domain: string): void {
    if (this.internalLinksRefreshing.get(domain)) return;
    if (!this.canRefreshInternalLinks(domain)) {
      this.notifications.error(this.internalLinksPrerequisiteHint(domain) || 'Prerequisites not met.');
      return;
    }
    this.internalLinksRefreshing.set(domain, true);

    this.enrichmentSubs.add(
      this.http.post<{ success: boolean; message?: string; data?: any }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/internal-link-suggestions`,
        {}
      ).subscribe({
        next: (res) => {
          this.internalLinksRefreshing.set(domain, false);
          this.notifications.success(res?.message || 'Internal link suggestions build queued.');
          this.loadCrawlStatus(domain);
        },
        error: (err) => {
          this.internalLinksRefreshing.set(domain, false);
          this.notifications.error(err?.error?.message || err?.message || 'Failed to build internal link suggestions.');
        }
      })
    );
  }

  refreshKeywords(domain: string): void {
    if (this.keywordsRefreshing.get(domain)) return;
    this.keywordsRefreshing.set(domain, true);

    this.enrichmentSubs.add(
      this.http.post<{ success: boolean; message?: string; data?: any }>(
        `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/ranked-keywords-enrichment`,
        { force: true, maxPages: 500, offset: 0 }
      ).subscribe({
        next: (res) => {
          this.keywordsRefreshing.set(domain, false);
          const d = res?.data;
          const total = d?.matchedTrackedUrls ?? d?.processed ?? 0;
          const cost = Number.isFinite(d?.estimatedCostSum) ? `$${(d.estimatedCostSum as number).toFixed(3)}` : '—';
          this.notifications.success(
            `Ranked keywords updated: ${d?.updated ?? 0} of ${total} pages have keywords, cost ~${cost}.`
          );
          this.loadCrawlStatus(domain);
        },
        error: (err) => {
          this.keywordsRefreshing.set(domain, false);
          this.notifications.error(err?.error?.message || err?.message || 'Failed to refresh ranked keywords.');
        }
      })
    );
  }

  async deleteDomainData(domain: string): Promise<void> {
    const normalizedDomain = domain.toLowerCase().trim();
    const firstConfirm = window.confirm(
      `Delete ALL crawl and analysis data for ${normalizedDomain}?\n\nThis is destructive and cannot be undone.`
    );
    if (!firstConfirm) return;

    const typedDomain = (window.prompt(
      `Type "${normalizedDomain}" to confirm deletion`
    ) || '').toLowerCase().trim();

    if (typedDomain !== normalizedDomain) {
      this.error = `Deletion cancelled: confirmation did not match "${normalizedDomain}".`;
      return;
    }

    this.deletingDomains.set(domain, true);
    this.error = null;

    try {
      const response = await firstValueFrom(this.crawlService.deleteDomainData(domain, typedDomain));
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete domain data');
      }

      this.stopPolling(domain);
      this.domainStatuses.delete(domain);
      this.processingStatuses.delete(domain);
      this.trafficRefreshing.delete(domain);
      this.keywordsRefreshing.delete(domain);
      this.internalLinksRefreshing.delete(domain);
      this.supportingPagesByTopicRefreshing.delete(domain);
      this.supportingPagesOpportunitiesRefreshing.delete(domain);
      this.stopEnrichmentPolling(domain);
      this.failedPages.delete(domain);
      this.failedPagesLoading.delete(domain);
      this.noindexPagesExpanded.delete(domain);
      this.failedPagesExpanded.delete(domain);
      this.domains = this.domains.filter(item => item !== domain);
    } catch (error: any) {
      this.error = error?.error?.message || error?.message || 'Failed to delete domain data';
    } finally {
      this.deletingDomains.set(domain, false);
    }
  }
}

